import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightLeftIcon, CheckCircle2Icon, SmartphoneIcon, BuildingIcon, KeyIcon, MapPinIcon } from 'lucide-react';
interface PortingModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function PortingModal({
  isOpen,
  onClose
}: PortingModalProps) {
  const [step, setStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    phoneNumber: '',
    carrier: '',
    accountNumber: '',
    transferPin: '',
    zipCode: ''
  });
  const handleNext = () => {
    setStep(s => s + 1);
  };
  const simulateProcessing = (nextStep: number) => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setStep(nextStep);
    }, 1500);
  };
  const handleClose = () => {
    setStep(1);
    setFormData({
      phoneNumber: '',
      carrier: '',
      accountNumber: '',
      transferPin: '',
      zipCode: ''
    });
    onClose();
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-383" data-mp="2nt442bvkhrod">
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6" data-id="element-384" data-mp="2nvzxpo0dtu70">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} data-id="element-385" data-mp="2sutbzinhtp88" />
        
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-xl bg-white dark:bg-[#0E131F] rounded-3xl shadow-2xl border border-gray-200 dark:border-white/10 overflow-hidden flex flex-col max-h-[90vh]" data-id="element-386" data-mp="253nvr2dn4uxe">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-white/10 flex justify-between items-center shrink-0 bg-gray-50 dark:bg-black/50" data-id="element-387" data-mp="1md65r5k70dfq">
            <div className="flex items-center gap-3" data-id="element-388" data-mp="1qc58cjigby40">
              <ArrowRightLeftIcon className="w-5 h-5 text-blue-500" data-id="element-389" data-mp="2to3y6x4hp4m8" />
              <span className="font-black uppercase tracking-widest text-sm text-black dark:text-white" data-id="element-390" data-mp="2oqc725bo5mj3">
                Port Number
              </span>
            </div>
            <button onClick={handleClose} className="text-gray-400 hover:text-black dark:hover:text-white transition-colors" data-id="element-391" data-mp="33h1k7d8oxbpl">
              <XIcon className="w-5 h-5" data-id="element-392" data-mp="2aq1f0yymi1wc" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8 overflow-y-auto flex-1 custom-scrollbar" data-id="element-393" data-mp="25rghj7ar9wbl">
            
            {/* Progress Stepper */}
            {step < 4 && <div className="flex items-center justify-between relative mb-10" data-id="element-394" data-mp="2jsamffylhf5i">
                <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 bg-gray-100 dark:bg-white/5 -z-10 rounded-full" data-id="element-395" data-mp="2fnt6xsz9po0j"></div>
                <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-blue-500 -z-10 rounded-full transition-all duration-500" style={{
              width: `${(step - 1) / 2 * 100}%`
            }} data-id="element-396" data-mp="3r4jilrmgn81j"></div>
                
                {[1, 2, 3].map(s => <div key={s} className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${step > s ? 'bg-blue-500 border-blue-500 text-white' : step === s ? 'bg-black dark:bg-[#0E131F] border-blue-500 text-blue-500' : 'bg-white dark:bg-black border-gray-200 dark:border-white/10 text-gray-400'}`} data-id="element-397" data-mp="7iixpgzxw6b6">
                    {step > s ? <CheckCircle2Icon className="w-4 h-4" data-id="element-398" data-mp="qa3frp2g4wac" /> : s}
                  </div>)}
              </div>}

            {step === 1 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} className="space-y-6" data-id="element-399" data-mp="gguszwbr3s9y">
                <div className="text-center mb-8" data-id="element-400" data-mp="145xb15aarn4z">
                  <h3 className="text-2xl font-black uppercase tracking-widest text-black dark:text-white mb-2" data-id="element-401" data-mp="18z05dfmwf0m8">Check Eligibility</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-402" data-mp="3hh4a6unv8rm9">Enter the phone number you want to transfer to Effy Mobile.</p>
                </div>
                
                <div className="space-y-2" data-id="element-403" data-mp="37iogcjxgf492">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500" data-id="element-404" data-mp="37jgdw0am3c1z">Phone Number</label>
                  <div className="relative" data-id="element-405" data-mp="40n500ss3be5">
                    <SmartphoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-406" data-mp="40dca5gcmq4r" />
                    <input type="tel" value={formData.phoneNumber} onChange={e => setFormData({
                  ...formData,
                  phoneNumber: e.target.value
                })} className="w-full bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-white/10 rounded-xl pl-12 pr-4 py-4 text-lg font-mono focus:outline-none focus:border-blue-500 transition-colors text-black dark:text-white" placeholder="(555) 000-0000" data-id="element-407" data-mp="3v7zl0chxdish" />
                  </div>
                </div>

                <button onClick={() => simulateProcessing(2)} disabled={isProcessing || formData.phoneNumber.length < 10} className="w-full bg-blue-500 hover:bg-blue-600 text-white font-black uppercase tracking-widest py-4 rounded-xl transition-colors disabled:opacity-50 flex justify-center items-center mt-8" data-id="element-408" data-mp="ubyp6or8vw5j">
                  {isProcessing ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-409" data-mp="1d8glljh1gk84"></div> : 'Check Eligibility'}
                </button>
              </motion.div>}

            {step === 2 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} className="space-y-6" data-id="element-410" data-mp="1n1p593cft84a">
                <div className="text-center mb-8" data-id="element-411" data-mp="1wxs3hy464mly">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 text-green-500 text-[10px] font-black uppercase tracking-widest mb-4" data-id="element-412" data-mp="1wyk0nddeysec">
                    <CheckCircle2Icon className="w-3 h-3" data-id="element-413" data-mp="12bbe4pvqivpy" /> Eligible to Transfer
                  </div>
                  <h3 className="text-2xl font-black uppercase tracking-widest text-black dark:text-white mb-2" data-id="element-414" data-mp="1gzofk2dld6ru">Carrier Details</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-415" data-mp="1c7eqeokawaqm">We need your current account info to authorize the transfer.</p>
                </div>
                
                <div className="space-y-4" data-id="element-416" data-mp="1kzzy99me8x0o">
                  <div className="space-y-1" data-id="element-417" data-mp="1l3dx47vumo3d">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500" data-id="element-418" data-mp="1zx91p1oz1r8r">Current Carrier</label>
                    <div className="relative" data-id="element-419" data-mp="1uzqqf4nhezxl">
                      <BuildingIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-420" data-mp="5jzmqudnpdj4" />
                      <select value={formData.carrier} onChange={e => setFormData({
                    ...formData,
                    carrier: e.target.value
                  })} className="w-full bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors text-black dark:text-white appearance-none" data-id="element-421" data-mp="ly6sibj1ubv">
                        <option value="" disabled data-id="element-422" data-mp="27fqg9mbpfw8l">Select Carrier</option>
                        <option value="verizon" data-id="element-423" data-mp="1th8ye58hddyl">Verizon</option>
                        <option value="tmobile" data-id="element-424" data-mp="1xk579487xvpc">T-Mobile</option>
                        <option value="att" data-id="element-425" data-mp="1xhj1990z7o98">AT&T</option>
                        <option value="other" data-id="element-426" data-mp="1jkw2r1tdkq17">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1" data-id="element-427" data-mp="1ol1oy2gvlkbw">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500" data-id="element-428" data-mp="3dxlx1knno194">Account Number</label>
                    <input type="text" value={formData.accountNumber} onChange={e => setFormData({
                  ...formData,
                  accountNumber: e.target.value
                })} className="w-full bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:border-blue-500 transition-colors text-black dark:text-white" placeholder="Usually found on your bill" data-id="element-429" data-mp="3ivnj8egdogot" />
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-430" data-mp="1i01azuf8fco7">
                    <div className="space-y-1" data-id="element-431" data-mp="1i2ngloikbi3s">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 flex items-center gap-1" data-id="element-432" data-mp="1dy5z5vzodgw6"><KeyIcon className="w-3 h-3" data-id="element-433" data-mp="190mn92qg9g7e" /> Transfer PIN</label>
                      <input type="password" value={formData.transferPin} onChange={e => setFormData({
                    ...formData,
                    transferPin: e.target.value
                  })} className="w-full bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:border-blue-500 transition-colors text-black dark:text-white" placeholder="4-6 digits" data-id="element-434" data-mp="38cp1nyk7vke7" />
                    </div>
                    <div className="space-y-1" data-id="element-435" data-mp="3plht4v8m38x2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 flex items-center gap-1" data-id="element-436" data-mp="2rz2bntg6qb9i"><MapPinIcon className="w-3 h-3" data-id="element-437" data-mp="2wx3y8octkspq" /> Account ZIP</label>
                      <input type="text" value={formData.zipCode} onChange={e => setFormData({
                    ...formData,
                    zipCode: e.target.value
                  })} className="w-full bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:border-blue-500 transition-colors text-black dark:text-white" placeholder="ZIP Code" data-id="element-438" data-mp="31wqaovktq4xh" />
                    </div>
                  </div>
                </div>

                <button onClick={() => simulateProcessing(3)} disabled={isProcessing || !formData.carrier || !formData.accountNumber || !formData.transferPin} className="w-full bg-blue-500 hover:bg-blue-600 text-white font-black uppercase tracking-widest py-4 rounded-xl transition-colors disabled:opacity-50 flex justify-center items-center mt-8" data-id="element-439" data-mp="1b6n6n066qcyp">
                  {isProcessing ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-440" data-mp="x86xl2kuoade"></div> : 'Verify Details'}
                </button>
              </motion.div>}

            {step === 3 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} className="space-y-6" data-id="element-441" data-mp="28o405tlm2st3">
                <div className="text-center mb-8" data-id="element-442" data-mp="2dpkv78hezl1r">
                  <h3 className="text-2xl font-black uppercase tracking-widest text-black dark:text-white mb-2" data-id="element-443" data-mp="1z3bqsvxsp4m">Review & Submit</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-444" data-mp="3t77q2n62f8cx">Please confirm your transfer request details.</p>
                </div>
                
                <div className="bg-gray-50 dark:bg-white/5 rounded-2xl p-6 space-y-4 border border-gray-200 dark:border-white/10" data-id="element-445" data-mp="3jhefnphnsza9">
                  <div className="flex justify-between items-center border-b border-gray-200 dark:border-white/10 pb-4" data-id="element-446" data-mp="3ej3336zzcaoc">
                    <span className="text-xs font-bold uppercase tracking-widest text-gray-500" data-id="element-447" data-mp="3aiaxcnqbgnwt">Number to Port</span>
                    <span className="text-sm font-mono font-bold text-black dark:text-white" data-id="element-448" data-mp="30i0hswgt74eh">{formData.phoneNumber}</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-200 dark:border-white/10 pb-4" data-id="element-449" data-mp="13o5sggnd69ze">
                    <span className="text-xs font-bold uppercase tracking-widest text-gray-500" data-id="element-450" data-mp="yr70a3n9gd3u">Current Carrier</span>
                    <span className="text-sm font-bold text-black dark:text-white capitalize" data-id="element-451" data-mp="2t4rlgjz5majb">{formData.carrier}</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-200 dark:border-white/10 pb-4" data-id="element-452" data-mp="2j9hgbndgln1b">
                    <span className="text-xs font-bold uppercase tracking-widest text-gray-500" data-id="element-453" data-mp="2o811vmo54wt5">Account Number</span>
                    <span className="text-sm font-mono text-black dark:text-white" data-id="element-454" data-mp="3bv0twbljayjg">••••{formData.accountNumber.slice(-4)}</span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-455" data-mp="31zpctmmnlf8j">
                    <span className="text-xs font-bold uppercase tracking-widest text-gray-500" data-id="element-456" data-mp="36y0nfzkrvtrt">Transfer PIN</span>
                    <span className="text-sm font-mono text-black dark:text-white" data-id="element-457" data-mp="1qj97im4tceze">••••</span>
                  </div>
                </div>

                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4" data-id="element-458" data-mp="1vkf1soe4gab1">
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-medium leading-relaxed" data-id="element-459" data-mp="djxigbhwl7au">
                    By submitting this request, you authorize Effy Mobile to transfer your service from your current provider. Do not cancel your current service until the transfer is complete.
                  </p>
                </div>

                <button onClick={() => simulateProcessing(4)} disabled={isProcessing} className="w-full bg-[#13E200] hover:bg-[#10c000] text-black font-black uppercase tracking-widest py-4 rounded-xl transition-colors disabled:opacity-50 flex justify-center items-center mt-8 shadow-[0_0_20px_rgba(19,226,0,0.3)]" data-id="element-460" data-mp="iih9urf9nbal">
                  {isProcessing ? <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" data-id="element-461" data-mp="37c4lh8htnvt6"></div> : 'Submit Port Request'}
                </button>
              </motion.div>}

            {step === 4 && <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="text-center py-8" data-id="element-462" data-mp="3g0tf1bukznoi">
                <div className="w-24 h-24 bg-[#13E200]/20 rounded-full flex items-center justify-center mx-auto mb-8 relative" data-id="element-463" data-mp="3kz4p9nosg079">
                  <div className="absolute inset-0 bg-[#13E200]/20 rounded-full animate-ping" data-id="element-464" data-mp="3uoo6kowurm00"></div>
                  <ArrowRightLeftIcon className="w-10 h-10 text-[#13E200] relative z-10" data-id="element-465" data-mp="3idbraifrhh4" />
                </div>
                <h3 className="text-3xl font-black uppercase tracking-tighter text-black dark:text-white mb-2" data-id="element-466" data-mp="7jov4yapv6gv">Request Submitted</h3>
                <p className="text-gray-500 dark:text-gray-400 font-medium mb-8 max-w-sm mx-auto" data-id="element-467" data-mp="ckcv49gxaxn8">
                  Your port request for <span className="text-black dark:text-white font-mono" data-id="element-468" data-mp="hhd0ei5y7wda">{formData.phoneNumber}</span> has been sent to your current carrier. 
                </p>
                
                <div className="bg-gray-50 dark:bg-white/5 rounded-2xl p-6 text-left mb-8 space-y-3" data-id="element-469" data-mp="2jaandxd1nuqb">
                  <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-4" data-id="element-470" data-mp="2bqryjah4d79t">What happens next?</p>
                  <div className="flex items-start gap-3 text-sm font-medium text-black dark:text-white" data-id="element-471" data-mp="26xf94y4m8wg0">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-500 flex items-center justify-center shrink-0 text-xs font-bold" data-id="element-472" data-mp="1x13y0bgricn5">1</div>
                    <p data-id="element-473" data-mp="2unjb7125oo4w">Keep your current SIM in your phone until it loses service.</p>
                  </div>
                  <div className="flex items-start gap-3 text-sm font-medium text-black dark:text-white" data-id="element-474" data-mp="2pqaxbco1j69g">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-500 flex items-center justify-center shrink-0 text-xs font-bold" data-id="element-475" data-mp="2kotwti9gxlte">2</div>
                    <p data-id="element-476" data-mp="19brqrh4zwenh">Transfers usually take 10-15 minutes, but can take up to 24 hours.</p>
                  </div>
                  <div className="flex items-start gap-3 text-sm font-medium text-black dark:text-white" data-id="element-477" data-mp="14bdji1b7x8qi">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-500 flex items-center justify-center shrink-0 text-xs font-bold" data-id="element-478" data-mp="3sjoqduqvjtz5">3</div>
                    <p data-id="element-479" data-mp="3ikg0bcz4qzi2">We'll notify you via email when the transfer is complete.</p>
                  </div>
                </div>

                <button onClick={handleClose} className="w-full bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black font-black uppercase tracking-widest py-4 rounded-xl transition-colors" data-id="element-480" data-mp="3ni7r23o1gfko">
                  Return to Dashboard
                </button>
              </motion.div>}

          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}